/** Pure template-string renderers for institutional-subscription account states.
 *
 * Layer: `view` per docs/design/spa-account-extraction-260524.md §3.2.
 *
 * Covers:
 *   - institutional-flat state  (renderFlatState)
 *   - institutional-invoiced state  (renderInvoicedState)
 *   - renderMonthlyUsageSection + renderPersonalBalanceSection wrappers
 *
 * Purity contract: every exported function accepts typed data and returns a
 * string. No DOM access, no fetch, no window.X mutations.
 *
 * Extracted from web/static/account.html in Phase AC.4.
 */
import { pickPersonalGroup, creditBalanceOf } from './model.js';
import { renderAccountInfoSection, renderTokenTxSection, fmtDate, fmtMonth, nextMonthFirst, renderMonthlyUsageSectionInternal, renderPersonalBalanceSectionInternal, } from './view-usage.js';
// ─── Monthly usage section (public API) ──────────────────────────────────
export function renderMonthlyUsageSection(esc, usage, headerLine, isCapHit) {
    return renderMonthlyUsageSectionInternal(esc, usage, headerLine, isCapHit);
}
// ─── Personal balance section (public API) ────────────────────────────────
export function renderPersonalBalanceSection(esc, credits, opts) {
    return renderPersonalBalanceSectionInternal(esc, credits, opts);
}
// ─── Flat-billing institutional state ────────────────────────────────────
export function renderFlatState(esc, data) {
    const u = (data.user || {});
    let flatGroup = (data.groups || []).find(g => g != null && g.unlimited_mode === 'flat') ?? null;
    // Backward-compat: v1-shape ships institution name on data.institution when
    // groups[] hasn't been wired yet.
    const dataExt = data;
    if (!flatGroup && dataExt.institution)
        flatGroup = { name: dataExt.institution.name };
    if (!flatGroup)
        flatGroup = {};
    const personal = pickPersonalGroup(data);
    const credits = creditBalanceOf(personal, data.user);
    const usage = data.monthly_usage || {};
    const cur = new Date();
    const resetText = `${esc(fmtMonth(cur))} — resets on ${esc(fmtDate(nextMonthFirst(cur).toISOString()))}`;
    return `
        <div class="header-row-grid">
            ${renderAccountInfoSection(esc, u, { userClass: 'institutional', institutionName: flatGroup.name || '' })}
            <section class="account-section" data-testid="account-tokens" aria-labelledby="inst-flat-h">
                <h2 id="inst-flat-h">Group Access</h2>
                <div class="institutional-unlimited-panel" role="status" data-testid="institutional-unlimited" aria-label="Institutional access status">
                    <span class="tier-badge institutional" style="margin-bottom:8px;display:inline-block">Institutional</span>
                    <p style="font-size:14px;margin-top:6px">
                        You have access via <strong>${esc(flatGroup.name || 'your institution')}</strong>.
                        Your usage draws from the group pool.
                    </p>
                </div>
            </section>
        </div>
        ${renderMonthlyUsageSectionInternal(esc, usage, resetText, false)}
        ${renderPersonalBalanceSectionInternal(esc, credits, { allowBuy: true })}
        ${renderTokenTxSection()}
    `;
}
// ─── Invoiced institutional state ─────────────────────────────────────────
export function renderInvoicedState(esc, data) {
    const u = (data.user || {});
    const invGroup = (data.groups || []).find(g => g != null && g.unlimited_mode === 'invoiced') ?? {};
    const personal = pickPersonalGroup(data);
    const credits = creditBalanceOf(personal, data.user);
    const usage = data.monthly_usage || {};
    const cur = new Date();
    const resetText = `${esc(fmtMonth(cur))} — resets on ${esc(fmtDate(nextMonthFirst(cur).toISOString()))}`;
    return `
        <div class="header-row-grid">
            ${renderAccountInfoSection(esc, u, {
        userClass: 'institutional',
        institutionName: invGroup.name || '',
        billingMode: 'Invoiced (off-platform contract)',
    })}
            <section class="account-section" data-testid="account-tokens" aria-labelledby="inst-inv-h">
                <h2 id="inst-inv-h">Group Access</h2>
                <div class="institutional-unlimited-panel" role="status" data-testid="institutional-unlimited">
                    <span class="tier-badge institutional" style="margin-bottom:8px;display:inline-block">Institutional (Invoiced)</span>
                    <p style="font-size:14px;margin-top:6px">
                        You have access via <strong>${esc(invGroup.name || 'your institution')}</strong>.
                        Capacity is managed by your institution admin under a direct agreement.
                    </p>
                </div>
            </section>
        </div>
        ${renderMonthlyUsageSectionInternal(esc, usage, resetText, false)}
        ${renderPersonalBalanceSectionInternal(esc, credits, { allowBuy: false })}
        ${renderTokenTxSection()}
    `;
}
